import _ from "lodash";
import db from "@/db";

import useInstallationVle from "@/composables/installations/vles/useInstallationVle";

const { getDewPoint } = useInstallationVle();

const findParametersUsed = async (phase) => {
  if (!phase) return [];
  const samplesUsed = await db.samples.where("phase").equals(phase).toArray();
  const parameterUsedUuids = _.flatten(
    samplesUsed.map((p) => [...p.mainParameters, p.secondParameter])
  ).filter((uuid) => !!uuid);

  return await db.parameters.where("uuid").anyOf(parameterUsedUuids).toArray();
};

const findVleUsed = async (
  installation,
  { phase = null, parametersUsed = null }
) => {
  if (!parametersUsed) {
    parametersUsed = await findParametersUsed(phase);
  }
  const parameterUsedUuids = parametersUsed.map(({ uuid }) => uuid);

  const mtxIsUsed = parametersUsed.some((p) =>
    ["MtxP", "MtxG"].includes(p.sampleShortName)
  );

  const configurationLineUuids = await db.configurationLines
    .where("parameter")
    .anyOf(parameterUsedUuids)
    .and((cl) => cl.installation == installation)
    .primaryKeys();

  let vlesConfig = await db.vles
    .where("configurationLine")
    .anyOf(configurationLineUuids)
    .toArray();

  let vlesSums = [];
  if (mtxIsUsed) {
    const sums = await db.sums
      .where("installation")
      .equals(installation)
      .primaryKeys();

    vlesSums = await db.vles.where("sum").anyOf(sums).toArray();
  }

  return [...vlesConfig, ...vlesSums];
};

const findFlows = async (installation, phase) => {
  const vles = await findVleUsed(installation, { phase });
  const vleUuids = vles.map(({ uuid }) => uuid);
  return await db.flows.where("vle").anyOf(vleUuids).toArray();
};

const getFilterTemperature = async (installationUuid, phase, tubeMaterial) => {
  const installation = await db.installations.get({ uuid: installationUuid });
  const h2oExpectedContent = installation.h2oExpectedContent;
  let dewPoint = 0;
  if (h2oExpectedContent) dewPoint = getDewPoint(h2oExpectedContent);
  const parameters = await findParametersUsed(phase);
  const temperatures = parameters.map((p) =>
    p.getFilteringTemperature(tubeMaterial, dewPoint)
  );
  return temperatures.length ? Math.max(...temperatures) : 0;
};

const manualSamplingRecipe = {
  type: "manual_sampling",
  recordForm: {
    // step 1
    samplingDuration: null,
    amoniumSalt: false,
    tubeMaterial: "TITANIUM",
    realTemperature: 1,
    theoricTemperature: 0,
    isValidatedStep1: false,
    filtrationMode: "EX",

    // step2
    meters: null,
    thermocouple: null,
    pitotTube: null,
    balance: null,
    masseEtalon: null,
    buse: null,
    realBuseDiameter: 0,
    realRateOfFlow: 0,
    isValidatedStep2: false,

    // step3
    measurementForm: null,
  },
  recordBlocks: [],

  formFieldToEvent: {
    interventionDate: { type: "INTERVENTION_DATE", unique: true },
    samplingDuration: { type: "DURATION", unique: true },
    amoniumSalt: { type: "AMONIUM_SALT", unique: true },
    tubeMaterial: { type: "TUBE", unique: true },
    realTemperature: { type: "TEMPERATURE", unique: true },
  },
  premix: [
    "recordForm.meters",
    "recordForm.thermocouple",
    "recordForm.pitotTube",
    "recordForm.buse",
    "recordForm.realBuseDiameter",
    "recordForm.realRateOfFlow",
    "recordForm.balance",
    "recordForm.masseEtalon",
    "recordForm.filtrationMode",
    "recordForm.tubeMaterial",
  ],
};

const manualSamplingRecipeMessages = {
  fr: {
    "manual_sampling.manual_sampling_general_informations_form":
      "Informations générales",
    "manual_sampling.manual_sampling_material_form": "Matériels",
    "manual_sampling.manual_sampling_notebook_form": "Recueil des essais",
    "manual_sampling.block.manual_sampling_general_informations_form":
      "Initialisation",
    "manual_sampling.block.manual_sampling_material_form": "Choix du matériel",
    "manual_sampling.block.manual_sampling_notebook_form": "Mesures",
  },
  en: {
    "manual_sampling.manual_sampling_general_informations_form":
      "General informations",
    "manual_sampling.manual_sampling_material_form": "Materials",
    "manual_sampling.manual_sampling_notebook_form": "Sampling's notebook",
    "manual_sampling.block.manual_sampling_general_informations_form":
      "Initialisation",
    "manual_sampling.block.manual_sampling_material_form": "Materials pick",
    "manual_sampling.block.manual_sampling_notebook_form": "Samplings",
  },
};

export {
  manualSamplingRecipe,
  manualSamplingRecipeMessages,
  getFilterTemperature,
  findFlows,
};
